import { useEffect, useState } from 'react';
import client from '../api/client';
import { messaggioErrore } from '../utils/apiError';

// Frasi per Alexa con le istruzioni tra parentesi (03/10/2026): «(sussurra) Giorgio… ti dico un segreto (pausa)
// (normale) sei fortissimo! (evviva) (applauso)». Qui i pezzi si vedono come bottoni e si compongono senza
// ricordare le parole; sotto resta la stessa frase che si scrive da WhatsApp. Il server (alexa_voce.py) la
// trasforma in quello che Alexa capisce.
const field = 'w-full border rounded-lg px-3 py-2 bg-surface text-sm';
const bottone = 'px-3 py-2 rounded-lg border text-sm bg-surface';
const SINONIMI = { sussurrato: 'sussurra', sottovoce: 'sussurra', piano: 'lento', adagio: 'lento', svelto: 'veloce', alto: 'forte', urla: 'forte' };
const SEGNI = { sussurra: '🤫', lento: '🐢', veloce: '⚡', acuto: '🎈', grave: '🎻', forte: '📢' };
const VUOTO = { stili: [{ valore: 'normale', etichetta: 'Normale' }], voci: [{ valore: '', etichetta: 'Alexa' }],
  pause: [{ valore: 'pausa', secondi: 0.6 }], suoni: [], esclamazioni: [] };

const chiave = t => t.trim().toLocaleLowerCase('it').split(/\s+/).join(' ');

// La frase divisa in pezzi, come fa il server: testo (col suo modo e la sua voce), pausa, suono, esclamazione,
// «dove» per «(su giorgio)». Una parentesi che non è un'istruzione resta testo.
export function pezziDi(frase, cat = VUOTO) {
  const stili = new Set(cat.stili.map(s => s.valore));
  const voci = new Set(cat.voci.map(v => v.valore).filter(Boolean));
  const pause = new Set(cat.pause.map(p => p.valore));
  const suoni = new Set(cat.suoni);
  const esclamazioni = new Set(cat.esclamazioni);
  const pezzi = [];
  let stile = 'normale'; let voce = ''; let inizio = 0;
  const testo = t => {
    const pulito = t.split(/\s+/).filter(Boolean).join(' ');
    if (pulito) pezzi.push({ tipo: 'testo', testo: pulito, stile, voce });
  };
  for (const m of (frase || '').matchAll(/\(([^()]{1,40})\)/g)) {
    let k = chiave(m[1]);
    k = SINONIMI[k] || k;
    let nuovo = null;
    if (stili.has(k)) nuovo = ['stile', k];
    else if (k.startsWith('voce ') && (voci.has(k.slice(5)) || k.slice(5) === 'alexa')) nuovo = ['voce', k.slice(5) === 'alexa' ? '' : k.slice(5)];
    else if (pause.has(k)) nuovo = ['pausa', k];
    else if (suoni.has(k)) nuovo = ['suono', k];
    else if (esclamazioni.has(k)) nuovo = ['esclamazione', k];
    else if (/^(su|a|ad|in) .{1,30}$/.test(k)) nuovo = ['dove', k.split(' ').slice(1).join(' ')];
    if (!nuovo) continue;
    testo(frase.slice(inizio, m.index));
    inizio = m.index + m[0].length;
    const [tipo, valore] = nuovo;
    if (tipo === 'stile') stile = valore;
    else if (tipo === 'voce') voce = valore;
    else pezzi.push({ tipo, nome: valore });
  }
  testo((frase || '').slice(inizio));
  return pezzi;
}

// Il suono d'annuncio (04/10/2026): «(annuncio)» lo mette prima di una frase con effetti, «(senza annuncio)» lo
// toglie da una frase semplice, che altrimenti è un annuncio col suo suono. Sta fuori dai pezzi: è un interruttore.
const ANNUNCIO = /\(\s*(con annuncio|annuncio|senza annuncio)\s*\)\s*/gi;
export function separaAnnuncio(frase) {
  let annuncio = null;
  const resto = (frase || '').replace(ANNUNCIO, (_, k) => { annuncio = !/^senza/i.test(k); return ''; }).trim();
  return { annuncio, resto };
}
export const conAnnuncio = (annuncio, frase) => (annuncio === null ? frase : `(${annuncio ? 'annuncio' : 'senza annuncio'}) ${frase}`);
// Senza voci, toni, pause, suoni né esclamazioni: per Alexa è un annuncio normale.
export const semplice = pezzi => pezzi.every(p => p.tipo === 'dove' || (p.tipo === 'testo' && p.stile === 'normale' && !p.voce));

// I pezzi di nuovo in frase: un modo o una voce si scrivono solo quando cambiano.
export function fraseDi(pezzi) {
  const parti = [];
  let stile = 'normale'; let voce = '';
  for (const p of pezzi) {
    if (p.tipo === 'testo') {
      if ((p.voce || '') !== voce) { parti.push(`(voce ${p.voce || 'alexa'})`); voce = p.voce || ''; }
      if (p.stile !== stile) { parti.push(`(${p.stile})`); stile = p.stile; }
      parti.push(p.testo);
    } else parti.push(p.tipo === 'dove' ? `(su ${p.nome})` : `(${p.nome})`);
  }
  return parti.join(' ');
}

function nomePezzo(p, cat) {
  if (p.tipo === 'testo') {
    const voce = p.voce ? `(${cat.voci.find(v => v.valore === p.voce)?.etichetta || p.voce}) ` : '';
    const testo = p.testo.length > 28 ? `${p.testo.slice(0, 27)}…` : p.testo;
    return `${SEGNI[p.stile] ? `${SEGNI[p.stile]} ` : ''}${voce}${testo}`;
  }
  if (p.tipo === 'pausa') return `⏸ ${String(cat.pause.find(x => x.valore === p.nome)?.secondi ?? '').replace('.', ',')} s`;
  if (p.tipo === 'suono') return `🔔 ${p.nome}`;
  if (p.tipo === 'esclamazione') return `🎉 ${p.nome}`;
  return `📍 su ${p.nome}`;
}

// Le Alexa, cosa si può mettere tra parentesi e le frasi salvate, dal server; letti una volta. Le frasi salvate
// cambiano: chi le salva o le cancella avvisa tutti gli editor aperti.
let memoria = null;
const ascoltatori = new Set();
export function useParla() {
  const [dati, setDati] = useState(memoria);
  useEffect(() => {
    ascoltatori.add(setDati);
    if (!memoria) {
      client.get('/v1/home-assistant/parla/')
        .then(({ data }) => { memoria = data; ascoltatori.forEach(f => f(data)); })
        .catch(() => setDati({ voci: [], catalogo: VUOTO, frasi: [], errore: true }));
    }
    return () => { ascoltatori.delete(setDati); };
  }, []);
  return dati;
}
export function aggiornaFrasi(frasi) {
  memoria = { ...(memoria || { voci: [], catalogo: VUOTO }), frasi };
  ascoltatori.forEach(f => f(memoria));
}
export const _dimentica = () => { memoria = null; };

export function SceltaAlexa({ voci, valore, onChange, etichetta = 'Quale Alexa' }) {
  return <label className="block text-sm">{etichetta}
    <select className={field} value={valore} onChange={e => onChange(e.target.value)}>
      <option value="">Ovunque (tutte)</option>
      {(voci || []).filter(v => !v.valore.includes('ovunque')).map(v => <option key={v.valore} value={v.valore}>{v.etichetta}</option>)}
    </select>
  </label>;
}

export default function FraseAlexa({ valore, onChange, etichetta = 'Frase', vuota = '', prova = true, voce = '', salva = false }) {
  const dati = useParla();
  const cat = dati?.catalogo || VUOTO;
  const { annuncio, resto } = separaAnnuncio(valore);
  const pezzi = pezziDi(resto, cat);
  const conSuono = annuncio ?? semplice(pezzi);
  const [nome, setNome] = useState(null); // il nome mentre si salva la frase
  const [salvando, setSalvando] = useState(false);
  const [aperto, apri] = useState(null);
  // Il testo che si sta scrivendo: la frase rifatta dai pezzi perderebbe lo spazio appena battuto.
  const [bozza, setBozza] = useState(null);
  const setAperto = i => { setBozza(null); apri(i); };
  const [aMano, setAMano] = useState(false);
  const [dove, setDove] = useState(voce);
  const [esito, setEsito] = useState('');
  const [errore, setErrore] = useState('');
  const [inCorso, setInCorso] = useState(false);

  const cambia = nuovi => { setEsito(''); onChange(conAnnuncio(annuncio, fraseDi(nuovi))); };
  // Il suono d'annuncio: una frase semplice ce l'ha già (togliendolo si scrive «(senza annuncio)»), una con
  // effetti no (mettendolo si scrive «(annuncio)»).
  const suono = si => { setEsito(''); onChange(conAnnuncio(si === semplice(pezzi) ? null : si, fraseDi(pezzi))); };
  const salvaFrase = async () => {
    setErrore(''); setSalvando(true);
    try {
      const { data } = await client.post('/v1/home-assistant/frasi/', { nome, frase: valore || '' });
      aggiornaFrasi(data); setNome(null); setEsito(`Salvata come «${nome.trim()}».`);
    } catch (e) { setErrore(messaggioErrore(e, 'Frase non salvata.')); }
    finally { setSalvando(false); }
  };
  // Un testo dopo un testo con lo stesso modo e la stessa voce diventa un pezzo solo: si apre quello.
  const aggiungi = p => { const nuovi = [...pezzi, p]; cambia(nuovi); setAperto(pezziDi(fraseDi(nuovi), cat).length - 1); };
  const modifica = (i, patch) => cambia(pezzi.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  // Una voce scelta per un pezzo vale anche per il resto della frase che aveva la stessa voce (03/10/2026: «fagli
  // dire anche il resto a lui»); si ferma dove comincia un'altra voce.
  const cambiaVoce = (i, voce) => {
    const prima = pezzi[i].voce || '';
    let ferma = false;
    cambia(pezzi.map((p, j) => {
      if (j < i || p.tipo !== 'testo' || ferma) return p;
      if (j > i && (p.voce || '') !== prima) { ferma = true; return p; }
      return { ...p, voce };
    }));
  };
  const togli = i => { cambia(pezzi.filter((_, j) => j !== i)); setAperto(null); };
  const sposta = (i, verso) => {
    const j = i + verso;
    if (j < 0 || j >= pezzi.length) return;
    const nuovi = [...pezzi];
    [nuovi[i], nuovi[j]] = [nuovi[j], nuovi[i]];
    cambia(nuovi); setAperto(j);
  };
  const ultimo = [...pezzi].reverse().find(p => p.tipo === 'testo');

  const proviamo = async () => {
    // Alexa parla davvero in casa: si chiede prima (di notte Giorgio dorme).
    if (!window.confirm('Alexa parlerà davvero in casa. La faccio parlare?')) return;
    setErrore(''); setEsito(''); setInCorso(true);
    try {
      const { data } = await client.post('/v1/home-assistant/parla/', { voce: dove, frase: valore || '' });
      setEsito(data.text);
    } catch (e) { setErrore(messaggioErrore(e, 'Alexa non ha parlato.')); }
    finally { setInCorso(false); }
  };

  const p = aperto !== null ? pezzi[aperto] : null;
  return <fieldset className="border rounded-lg p-3 space-y-3">
    <legend className="px-1 font-medium">{etichetta}</legend>
    <div className="flex flex-wrap gap-2" aria-label="Pezzi della frase">
      {pezzi.map((x, i) => <button type="button" key={`${i}-${x.tipo}`} aria-pressed={aperto === i}
        className={`${bottone} ${aperto === i ? 'border-brand-600 bg-brand-50' : ''}`} onClick={() => setAperto(aperto === i ? null : i)}>
        {nomePezzo(x, cat)}
      </button>)}
      {!pezzi.length && <span className="text-sm text-muted">{vuota || 'Vuota: aggiungi un pezzo qui sotto.'}</span>}
    </div>

    {p && <div className="border rounded-lg p-3 space-y-2" aria-label="Modifica del pezzo">
      {p.tipo === 'testo' && <>
        <label className="block text-sm">Cosa dice
          <input className={field} value={bozza ?? p.testo} onChange={e => {
            setBozza(e.target.value);
            // Vuoto, il pezzo sparirebbe: resta l'ultimo testo finché non si scrive altro o si toglie.
            if (e.target.value.trim()) modifica(aperto, { testo: e.target.value.replace(/[()]/g, ' ') });
          }} /></label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-sm">Come lo dice
            <select className={field} value={p.stile} onChange={e => modifica(aperto, { stile: e.target.value })}>
              {cat.stili.map(s => <option key={s.valore} value={s.valore}>{s.etichetta}</option>)}
            </select></label>
          <label className="block text-sm">Con che voce
            <select className={field} value={p.voce || ''} onChange={e => cambiaVoce(aperto, e.target.value)}>
              {cat.voci.map(v => <option key={v.valore} value={v.valore}>{v.etichetta}</option>)}
            </select></label>
        </div>
      </>}
      {p.tipo !== 'testo' && <label className="block text-sm">{{ pausa: 'Quanto', suono: 'Quale suono', esclamazione: 'Quale esclamazione', dove: 'Su quale Alexa' }[p.tipo]}
        {p.tipo === 'dove'
          ? <input className={field} value={p.nome} onChange={e => modifica(aperto, { nome: e.target.value })} />
          : <select className={field} value={p.nome} onChange={e => modifica(aperto, { nome: e.target.value })}>
            {(p.tipo === 'pausa' ? cat.pause.map(x => x.valore) : p.tipo === 'suono' ? cat.suoni : cat.esclamazioni)
              .map(n => <option key={n} value={n}>{p.tipo === 'pausa' ? `${String(cat.pause.find(x => x.valore === n)?.secondi).replace('.', ',')} secondi` : n}</option>)}
          </select>}
      </label>}
      <div className="flex gap-2">
        <button type="button" className={bottone} onClick={() => sposta(aperto, -1)} disabled={aperto === 0} aria-label="Sposta prima">↑</button>
        <button type="button" className={bottone} onClick={() => sposta(aperto, 1)} disabled={aperto === pezzi.length - 1} aria-label="Sposta dopo">↓</button>
        <button type="button" className={bottone} onClick={() => togli(aperto)}>✕ Togli</button>
        <button type="button" className={`${bottone} ml-auto`} onClick={() => setAperto(null)}>Fatto</button>
      </div>
    </div>}

    <div className="flex flex-wrap gap-2">
      <button type="button" className={bottone} onClick={() => aggiungi({ tipo: 'testo', testo: 'Ciao', stile: ultimo?.stile || 'normale', voce: ultimo?.voce || '' })}>＋ Testo</button>
      <button type="button" className={bottone} onClick={() => aggiungi({ tipo: 'pausa', nome: cat.pause[0].valore })}>＋ Pausa</button>
      {cat.suoni.length > 0 && <button type="button" className={bottone} onClick={() => aggiungi({ tipo: 'suono', nome: cat.suoni[0] })}>＋ Suono</button>}
      {cat.esclamazioni.length > 0 && <button type="button" className={bottone} onClick={() => aggiungi({ tipo: 'esclamazione', nome: cat.esclamazioni[0] })}>＋ Esclamazione</button>}
    </div>

    {pezzi.length > 0 && <label className="flex items-start gap-2 text-sm">
      <input type="checkbox" className="mt-1" checked={conSuono} onChange={e => suono(e.target.checked)} />
      <span>Suono d’annuncio prima
        <span className="block text-xs text-muted">{semplice(pezzi)
          ? 'Una frase semplice è un annuncio di Alexa, col suo suono.'
          : 'Con voci e toni Alexa non fa l’annuncio: si mette prima un suono simile.'}</span></span>
    </label>}

    {salva && pezzi.length > 0 && (nome === null
      ? <button type="button" className={bottone} onClick={() => setNome('')}>💾 Salva con un nome</button>
      : <form className="flex flex-wrap items-end gap-2" onSubmit={e => { e.preventDefault(); salvaFrase(); }}>
        <label className="block text-sm flex-1 min-w-[10rem]">Nome della frase
          <input className={field} value={nome} maxLength={60} autoFocus placeholder="Es. A tavola"
            onChange={e => setNome(e.target.value)} /></label>
        <button type="submit" className={bottone} disabled={!nome.trim() || salvando}>{salvando ? 'Salvo…' : 'Salva'}</button>
        <button type="button" className={bottone} onClick={() => setNome(null)}>Annulla</button>
      </form>)}

    <div>
      <button type="button" className="text-sm text-brand-700 underline" onClick={() => setAMano(!aMano)} aria-expanded={aMano}>
        {aMano ? 'Nascondi la frase scritta' : 'Scrivila a mano (come su WhatsApp)'}</button>
      {aMano && <textarea className={`${field} mt-2`} rows={3} aria-label="Frase scritta" value={valore || ''}
        onChange={e => { setEsito(''); onChange(e.target.value); }} />}
    </div>

    {prova && <div className="flex flex-wrap items-end gap-2">
      <div className="flex-1 min-w-[10rem]"><SceltaAlexa voci={dati?.voci} valore={dove} onChange={setDove} etichetta="Prova su" /></div>
      <button type="button" className={bottone} disabled={!pezzi.length || inCorso} onClick={proviamo}>{inCorso ? 'Parla…' : '▶ Prova'}</button>
    </div>}
    {esito && <p className="text-sm" role="status">{esito}</p>}
    {errore && <p className="text-sm text-danger" role="alert">{errore}</p>}
  </fieldset>;
}
